import { getMetricPrimaryColor, getMetricSecondaryColor } from "@Containers/app/stat-cards/tooltips/get-metric-colors";
import { RadarMetricInfo } from "@Helpers/types";
import useI18n from "@i18n";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer } from "recharts";

interface IRadialBarChartMobile {
  data: RadarMetricInfo[];
}

const CustomLegend = ({ theme, i18n }: { theme: any; i18n: any }) => {
  return (
    <Box position="absolute" top={-25} right={0} p={2}>
      <Stack direction="row" spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box width={8} height={8} borderRadius="25%" bgcolor={theme.palette.dsPrimary.v300} />
          <Typography variant="textXs500" color="text.secondary">
            {i18n("progress.skill-radar.recent")}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box width={8} height={8} borderRadius="25%" bgcolor={theme.palette.dsWarning.v400} />
          <Typography variant="textXs500" color="text.secondary">
            {i18n("progress.skill-radar.previous")}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

const CustomTick = (props: any) => {
  const { x, y, payload } = props;
  const text = payload?.value as string;

  return (
    <g>
      <rect x={x - 20} y={y - 12} width={40} height={25} fill="white" rx={4} />
      <text x={x} y={y} dy={4} fontSize={16} fill="#666" textAnchor="middle">
        {text}
      </text>
    </g>
  );
};

const RadialBarChartMobile = ({ data }: IRadialBarChartMobile) => {
  const theme = useTheme();
  const { i18n } = useI18n();

  const transformedData = data.map((item) => ({
    ...item,
    subject: i18n(`metric.${item.name}.name.initials`),
    current: item.current || 0,
    previous: item.previous || 0,
  }));

  return (
    <Stack position="relative" width="100%" height={350} spacing={3}>
      <CustomLegend theme={theme} i18n={i18n} />
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart innerRadius="5%" outerRadius="82.5%" data={transformedData} cx="50%" cy="50%">
          <PolarGrid gridType="circle" radialLines={true} polarRadius={[147.5]} stroke={theme.palette.dsSurface.v300} />
          <PolarAngleAxis dataKey="subject" tick={<CustomTick />} />
          <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
          <Radar
            name={i18n("previous")}
            dataKey="previous"
            stroke={theme.palette.dsWarning.v300}
            strokeWidth={1}
            strokeOpacity={0.5}
            fill={theme.palette.dsWarning.v400}
            fillOpacity={0.3}
          />
          <Radar
            name={i18n("current")}
            dataKey="current"
            stroke={theme.palette.dsPrimary.v400}
            strokeWidth={1}
            strokeOpacity={0.5}
            fill={theme.palette.dsPrimary.v400}
            fillOpacity={0.3}
            dot={(props: any) => {
              const { cx, cy, payload } = props;
              const metricName = payload.payload.name;
              const outerDotColor = getMetricSecondaryColor(metricName, theme);
              return <circle cx={cx} cy={cy} r={0} fill={outerDotColor} fillOpacity={0.5} />;
            }}
          />
          <Radar
            name={i18n("current")}
            dataKey="current"
            stroke="transparent"
            fill="transparent"
            dot={(props: any) => {
              const { cx, cy, payload } = props;
              const metricName = payload.payload.name;
              const current = payload.payload.current;
              const previous = payload.payload.previous;

              let dotColor = getMetricPrimaryColor(metricName, theme);
              if (previous !== undefined && previous !== null) {
                dotColor =
                  current > previous
                    ? theme.palette.dsSuccess.v400
                    : current < previous
                    ? theme.palette.dsWarning.v400
                    : theme.palette.dsSuccess.v400;
              }

              return <circle cx={cx} cy={cy} r={5} fill={dotColor} />;
            }}
          />
        </RadarChart>
      </ResponsiveContainer>
    </Stack>
  );
};

export default RadialBarChartMobile;
